import { connect } from 'react-redux';

import { useHistory } from 'react-router';

import O2UserPopperInternal, { UserPopperInternalAccount } from '@o2/common-business/UserPopperInternal';
import Link from '@o2/ui/Link';

import { environment } from 'environments/environment';
import { AccessToEntityManaged, ExternalUserAccesses } from 'generated/bffUserAccess/models';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { useTranslationWithDataKey } from 'hooks/useTranslationWithDataKey';
import { ClearContextRequestActionInputs } from 'models/context/ClearContext';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { selectAccessExternalUserAccessesData } from 'saga/accessManagement/ducks';
import { AuthUserType, selectAuthUserType } from 'saga/auth/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { clearContextAction } from 'saga/context/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { selectProperty } from 'saga/property/ducks';
import { PopoverState } from './NewAppBarInternal';

interface Props {
  id: string;
  popoverElm: PopoverState;
  handlePopoverClose: () => void;
}

const UserPopperInternal = ({
  id,
  popoverElm,
  handlePopoverClose,
  maxDirectRolesCount,
  authUserType,
  redirectToLegacyCrm,
  ...props
}: Props & StateToProps & DispatchToProps) => {
  const { t } = useTranslationWithDataKey();
  const showAccesses = (props.userAccesses?.accessesToEntityManaged?.length || 0) <= (maxDirectRolesCount || 0);
  const history = useHistory();
  const missingName = environment.properties['gui.general.BFFDIGITVCAPTURE.contactPerson.missingName'];

  const handleUserVerify = () => {
    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'contactPersonVerification' },
      });
    }
  };

  const handleSettingsClick = () => {
    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'customerSettings' },
      });
    }
  };

  /* todo replace checkBooleanPropertyTemporary once yaml and bff for getting this property is ok */
  const handleAccessesClick = () => {
    if (authUserType === 'internal') {
      history.push(
        buildRoute(ROUTES.ContactPersonAccess, {
          cpRefNo: props.contextExternalUser?.cpRefNo || '',
        })
      );
    }
  };

  const linkToAccess = (access: AccessToEntityManaged) => {
    let deeplinkQueryParams = null;

    if (access.entityTypeId === 'Customer') {
      deeplinkQueryParams = { cuRefNo: access.entityRefNo };
    } else if (access.entityTypeId === 'CustomerAccount') {
      deeplinkQueryParams = { caRefNo: access.entityRefNo };
    } else if (access.entityTypeId === 'TariffSpace') {
      deeplinkQueryParams = { tsRefNo: access.entityRefNo };
    }

    if (deeplinkQueryParams) {
      if (authUserType === 'internal') {
        redirectToLegacyCrm({
          command: LegacyCrmDeeplinkCommand.IMO2DASH,
          queryParams: deeplinkQueryParams,
        });
      }
    }
  };

  const handleClearContext = () => {
    const clearContextPayload: ClearContextRequestActionInputs = {};

    if (authUserType === 'internal') {
      clearContextPayload.deeplinkAfterClearContext = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'internalDashboard' },
      };
    }

    props.clearContext(clearContextPayload);
  };

  return (
    <O2UserPopperInternal
      id={id}
      open={popoverElm.id === id}
      onClose={handlePopoverClose}
      targetElm={popoverElm.target as Element}
      verified={props.contextExternalUser?.isVerified}
      verifiedText={t('onecrm.gui_internalHeader.label.cpVerified', true)}
      unverifiedText={t('onecrm.gui_internalHeader.label.cpUnverified', true)}
      btnVerifiedText={t('onecrm.gui_internalHeader.action.cpVerifyButton', true)}
      onVerifiedBtnClick={handleUserVerify}
      onAccessBtnClick={handleAccessesClick}
      showAccessBtn={!!props.contextCustomer}
      btnAccessText={t('onecrm.gui_internalHeader.action.cpAccessesButton', true)}
      onSettingsBtnClick={handleSettingsClick}
      btnSettingsText={t('onecrm.gui_internalHeader.action.cpSettingsButton', true)}
      onLogoutBtnClick={handleClearContext}
      btnLogoutText={t('onecrm.gui_internalHeader.action.cpClearContextButton', true)}
      user={{
        id: props.contextExternalUser?.email,
        name: props.contextExternalUser?.firstName !== missingName && props.contextExternalUser?.displayName, //11.1.4_034
        mobile: props.contextExternalUser?.phoneNoFix,
        phone: props.contextExternalUser?.phoneNoMobile,
      }}
      accountList={
        showAccesses
          ? props.userAccesses?.accessesToEntityManaged?.map((access): UserPopperInternalAccount => {
              return {
                name: (
                  <>
                    {t(`onecrm.gui_internalHeader.label.cpEntityRoleType.${access.entityTypeId}`, false)}{' '}
                    <Link onClick={() => linkToAccess(access)}>{access.entityDescription}</Link>
                  </>
                ),
                role: <>{t(`onecrm.gui_entityManagement.access.label.roleType${access.roleType}`, true)}</>,
              };
            })
          : undefined
      }
      footerDescription={
        !showAccesses &&
        t('onecrm.gui_internalHeader.label.cpDirectRolesCount', true, {
          0: `${props.userAccesses?.accessesToEntityManaged?.length}`,
        })
      }
      footerLinkText={!showAccesses && t('onecrm.gui_internalHeader.action.cpAccessesLink', true)}
      onFooterLinkClick={handleAccessesClick}
    />
  );
};

interface StateToProps {
  contextExternalUser: ExternalUser | null;
  contextCustomer: CustomerInfo | null;
  userAccesses: ExternalUserAccesses | null;
  maxDirectRolesCount: number;
  authUserType: AuthUserType;
}

interface DispatchToProps {
  clearContext: (payload: ClearContextRequestActionInputs) => void;
  redirectToLegacyCrm: (deeplinkConfig: LegacyCrmDeeplinkConfig) => void;
}

const mapStateToProps = (state: AppState): StateToProps => ({
  contextExternalUser: selectContextExternalUser(state),
  contextCustomer: selectContextCustomerInfo(state),
  userAccesses: selectAccessExternalUserAccessesData(state),
  maxDirectRolesCount: Number(selectProperty('gui.accesses.maxDirectRolesCount')),
  authUserType: selectAuthUserType(state),
});

const mapDispatchToProps: DispatchToProps = {
  clearContext: clearContextAction.request,
  redirectToLegacyCrm: redirectToLegacyCrmAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserPopperInternal);
